import React, { useState, useCallback, useRef } from "react";
import { useDropzone } from "react-dropzone";
import * as XLSX from "xlsx";
import {
	Box,
	Typography,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	CircularProgress,
	LinearProgress,
	Stack,
	Checkbox,
	FormControlLabel,
	TextField,
	Collapse,
	Alert,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import { Warning } from "@mui/icons-material";
import DescriptionIcon from "@mui/icons-material/Description";
import ExistingRowConflictDialog from "./components/ExistingRowConflictDialog";
import useSubmitConflictResolution from "./hooks/SubmitConflictResolution";
import SnackbarNotification from "../../components/ui/SnackBarNotification";

const getCurrentBusinessPeriod = () => {
	const now = new Date();
	let year = now.getFullYear();
	let month = now.getMonth() + 1; // 1-12

	if (now.getDate() >= 16) {
		month += 1;
		if (month > 12) {
			month = 1;
			year += 1;
		}
	}

	return { year, month };
};

const getMaxHistoricalPeriod = () => {
	const { year, month } = getCurrentBusinessPeriod();
	let m = month - 1;
	let y = year;

	if (m < 1) {
		m = 12;
		y -= 1;
	}

	return `${y}-${String(m).padStart(2, "0")}`;
};

const ImportExcel = ({
	headline,
	sheetNames,
	apiUrl,
	handleStartLoading,
	handleStopLoading,
	showSnackbar,
	confirmMessage,
	sheetIndex = -1,
	// Fired once a cutoff has actually landed, so the list of imported
	// cutoffs below can pick it up without a page reload.
	onImported,
}) => {
	const [parsedData, setParsedData] = useState([]);
	const [files, setFiles] = useState([]);
	const [openConfirm, setOpenConfirm] = useState(false);
	const [confirmationMessage, setConfirmationMessage] = useState("");
	const [openConflictDialog, setOpenConflictDialog] = useState(false);
	const [duplicates, setDuplicates] = useState([]);
	// Policy rows the import would not take, and in-service agents it carried
	// nothing for. Both survive the conflict dialog, so they are still on screen
	// once conflicts are resolved.
	const [unmatchedRows, setUnmatchedRows] = useState(null);
	const [agentsWithoutRows, setAgentsWithoutRows] = useState(null);
	const [welcomeEmailFailures, setWelcomeEmailFailures] = useState(null);
	const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

	const [isHistoricalImport, setIsHistoricalImport] = useState(false);
	const [historicalPeriod, setHistoricalPeriod] = useState(""); // 'YYYY-MM'

	const { submitConflictResolution, loading: submitConflictLoading } = useSubmitConflictResolution();
	const maxHistoricalPeriod = getMaxHistoricalPeriod();

	const [isUploading, setIsUploading] = useState(false);
	const [uploadStats, setUploadStats] = useState({
		total: 0,
		successful: 0,
		failed: 0,
		isComplete: false,
		currentRecord: 0,
		isProcessing: false,
	});

	const fileInputRef = useRef(null);
	const progressTimerRef = useRef(null);

	const convertExcelDate = (serial) => {
		const excelEpoch = new Date(1900, 0, serial - 1);
		return excelEpoch.toISOString().split("T")[0];
	};

	// 'YYYY-MM' -> "March 2025"
	const formatPeriodReadable = (period) => {
		if (!period) return "";
		const [year, month] = period.split("-").map(Number);
		const date = new Date(year, month - 1, 1);
		return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
	};

	const handleSubmit = () => {
		if (!files.length) return;

		if (isHistoricalImport) {
			if (!historicalPeriod) {
				setSnackbar({ open: true, message: "Please select a period for this historical import.", severity: "error" });
				return;
			}

			if (historicalPeriod > maxHistoricalPeriod) {
				setSnackbar({
					open: true,
					message: `The selected period is the current or a future period. Please choose ${formatPeriodReadable(maxHistoricalPeriod)} or earlier.`,
					severity: "error",
				});
				return;
			}
		}

		const message = isHistoricalImport
			? `${confirmMessage} This will be imported as a cutoff for ${formatPeriodReadable(historicalPeriod)} and will not affect current live data.`
			: confirmMessage;

		setConfirmationMessage(message);
		setOpenConfirm(true);
	};

	const simulateProgressUpdates = (totalRecords) => {
		const step = Math.max(1, Math.floor(totalRecords / 20));
		const updateInterval = 500;
		let currentRecord = 0;
		const maxProgressBeforeComplete = totalRecords - step;

		const progressTimer = setInterval(() => {
			if (currentRecord < maxProgressBeforeComplete) {
				currentRecord = Math.min(currentRecord + step, maxProgressBeforeComplete);
				setUploadStats((prev) => ({
					...prev,
					currentRecord,
					isProcessing: true,
				}));
			} else {
				clearInterval(progressTimer);
				setUploadStats((prev) => ({
					...prev,
					currentRecord: maxProgressBeforeComplete,
					isProcessing: true,
				}));
			}
		}, updateInterval);

		return progressTimer;
	};

	const handleConfirm = async () => {
		setOpenConfirm(false);
		setIsUploading(true);

		const totalRecords = Object.values(parsedData).reduce((sum, sheetData) => sum + sheetData.length, 0);

		handleStartLoading();
		setUploadStats((prev) => ({ ...prev, isProcessing: true }));

		try {
			setUploadStats({
				total: totalRecords,
				successful: 0,
				failed: 0,
				isComplete: false,
				currentRecord: 0,
				isProcessing: false,
			});

			progressTimerRef.current = simulateProgressUpdates(totalRecords);

			const response = await fetch(apiUrl, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"X-WP-Nonce": window.wpApiSettings.nonce,
				},
				credentials: "include",
				body: JSON.stringify({
					data: parsedData,
					override_period: isHistoricalImport ? historicalPeriod : null,
				}),
			});

			const responseBody = await response.json();

			if (progressTimerRef.current) {
				clearInterval(progressTimerRef.current);
				progressTimerRef.current = null;
			}

			// A refused import comes back as a WP_Error — an HTTP status plus a `code`
			// and `message`. Without this it fell through as an empty payload and
			// reported a successful import that wrote nothing.
			if (!response.ok || responseBody?.code) {
				throw new Error(responseBody?.message || "Upload failed. Please try again.");
			}

			const payload = responseBody.result || {};
			const incomingDuplicates = payload.changed_bob || [];

			// Captured before the conflict branch returns, or it would be lost on any
			// workbook that also has conflicts to resolve.
			const unmatched = payload.warnings?.unmatched_rows ?? null;
			setUnmatchedRows(unmatched?.total > 0 ? unmatched : null);

			const noRows = payload.warnings?.agents_without_rows ?? null;
			setAgentsWithoutRows(noRows?.total > 0 ? noRows : null);

			// Only the ones that did not go out. A welcome that sent is not news;
			// one that did not means a brand-new agent is holding an account they
			// were never told about and cannot log into.
			const welcomeFailures = payload.warnings?.welcome_emails?.failed ?? [];
			setWelcomeEmailFailures(welcomeFailures.length > 0 ? welcomeFailures : null);

			if (incomingDuplicates.length > 0) {
				setDuplicates(incomingDuplicates);
				setIsUploading(false);
				setOpenConflictDialog(true);
				handleStopLoading();
				return;
			}

			const insertedBob = payload.inserted_bob ?? payload.snapshot_written ?? 0;
			const unchangedBob = payload.unchanged_bob || 0;
			const successful = insertedBob + unchangedBob;
			const failed = 0;
			const total = successful;

			setUploadStats({
				total,
				successful,
				failed,
				isComplete: false,
				currentRecord: total,
				isProcessing: true,
			});

			setTimeout(() => {
				setUploadStats({
					total,
					successful,
					failed,
					isComplete: true,
					currentRecord: total,
					isProcessing: false,
				});
			}, 1000);

			onImported?.();
		} catch (error) {
			console.error(error);

			if (progressTimerRef.current) {
				clearInterval(progressTimerRef.current);
				progressTimerRef.current = null;
			}

			setUploadStats((prev) => ({
				...prev,
				failed: prev.total,
				successful: 0,
				isComplete: true,
				currentRecord: prev.total,
				isProcessing: false,
			}));

			setTimeout(() => {
				setIsUploading(false);
				// The server's own words when it gave any — "this file has 14,000 rows,
				// an import handles up to 5,000" is actionable in a way "upload failed"
				// never is.
				showSnackbar(error?.message || "Upload failed. Please try again.", "error");
				resetUploadState();
			}, 3000);
		}

		handleStopLoading();
	};

	const handleConfirmSubmitResolution = async (payload) => {
		try {
			const result = await submitConflictResolution(payload);

			if (!result?.success) {
				setSnackbar({ open: true, message: "Failed to apply resolution. Please try again.", severity: "error" });
				return;
			}
			const { inserted = 0, updated = 0 } = result.result || {};
			const parts = [];
			if (updated > 0) parts.push(`${updated} updated`);
			if (inserted > 0) parts.push(`${inserted} added as new`);

			setSnackbar({
				open: true,
				message: parts.length ? `Resolved: ${parts.join(", ")}.` : "Imported and resolved sucessfully.",
			});
			setOpenConflictDialog(false);
			setDuplicates([]);
			handleDelete(0);
		} catch (error) {
			console.error(error);
			setSnackbar({ open: true, message: "Unexpected error while resolving conflicts.", severity: "error" });
		}
	};

	const resetUploadState = () => {
		setUploadStats({
			total: 0,
			successful: 0,
			failed: 0,
			isComplete: false,
			currentRecord: 0,
			isProcessing: false,
		});

		// Belong to the workbook that produced them, not the next one.
		setUnmatchedRows(null);
		setAgentsWithoutRows(null);
		setWelcomeEmailFailures(null);

		if (progressTimerRef.current) {
			clearInterval(progressTimerRef.current);
			progressTimerRef.current = null;
		}
	};

	const handleCancel = () => {
		setOpenConfirm(false);
	};

	const handleDelete = (idx) => {
		setFiles((prev) => prev.filter((_, i) => i !== idx));
		setParsedData([]);
		setIsHistoricalImport(false);
		setHistoricalPeriod("");
	};

	const onDrop = useCallback(
		(acceptedFiles) => {
			if (acceptedFiles.length > 0) {
				const file = acceptedFiles[0];
				setFiles([file]);

				const reader = new FileReader();
				reader.onload = (event) => {
					const data = new Uint8Array(event.target.result);
					const workbook = XLSX.read(data, { type: "array" });

					const categorizedData = {};
					workbook.SheetNames.forEach((sheetName, idx) => {
						if (idx !== sheetIndex && sheetIndex !== -1) {
							return;
						}
						const worksheet = workbook.Sheets[sheetName];
						let sheetData = XLSX.utils.sheet_to_json(worksheet);

						sheetData = sheetData.map((row) => ({
							...row,
							"DATE MAILED": typeof row["DATE MAILED"] === "number" ? convertExcelDate(row["DATE MAILED"]) : row["DATE MAILED"],
							DOI: typeof row.DOI === "number" ? convertExcelDate(row.DOI) : row.DOI,
						}));

						categorizedData[sheetName] = sheetData;
					});
					setParsedData(categorizedData);
				};

				reader.readAsArrayBuffer(file);
			}
		},
		[sheetIndex]
	);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		accept: {
			"text/csv": [".csv"],
			"application/vnd.ms-excel": [".xls"],
			"application/vnd.openxmlformats-officedocument.spreadsheetMl.sheet": [".xlsx"],
			"application/vnd.oasis.opendocument.spreadsheet": [".ods"],
		},
		onDrop,
		multiple: false,
		noKeyboard: true,
	});

	return (
		<Box>
			<Typography variant="h6" mb={1}>
				{headline}
			</Typography>
			<Box sx={{ position: "relative" }}>
				<Box
					{...getRootProps()}
					sx={{
						border: files.length > 0 ? "2px dashed #1D54A2" : isDragActive ? "2px dashed #1D54A2" : "2px dashed #ccc",
						backgroundColor: files.length > 0 ? "#f0f7ff !important" : isDragActive ? "#f0f7ff !important" : "#fafafa !important",
						borderRadius: "12px",
						height: 120,
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
						alignItems: "center",
						cursor: "pointer",
						transition: "all 0.3s ease",
						"&:hover": {
							borderColor: files.length > 0 ? "#1D54A2" : "#1D54A2",
							backgroundColor: files.length > 0 ? "#f0f7ff !important" : "#f8f9fa !important",
						},
					}}
				>
					<input {...getInputProps()} />

					{files.length === 0 ? (
						<>
							<CloudUploadIcon sx={{ fontSize: 28, color: isDragActive ? "#1D54A2" : "#999", mb: 1 }} />
							<Typography variant="body2" sx={{ color: isDragActive ? "#1D54A2" : "#666", fontSize: "14px" }}>
								{isDragActive ? "Drop your Excel file here" : "Click or drag Excel file here"}
							</Typography>
						</>
					) : (
						<>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									backgroundColor: "#fff",
									px: 2,
									py: 1,
									borderRadius: "12px",
									border: "none",
									minWidth: "60%",
									justifyContent: "space-between",
								}}
							>
								<Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
									<DescriptionIcon sx={{ fontSize: 18, color: "#1D54A2", mr: 1 }} />
									<Typography
										variant="body2"
										sx={{
											fontSize: "13px",
											color: "#333",
											whiteSpace: "nowrap",
											overflow: "hidden",
											textOverflow: "ellipsis",
											maxWidth: "200px",
										}}
									>
										{files[0]?.name}
									</Typography>
								</Box>

								<Button
									onClick={(e) => {
										e.stopPropagation();
										handleDelete(0);
									}}
									sx={{
										minWidth: "auto",
										p: 0.5,
										ml: 1,
										color: "#999",
										borderRadius: "50%",
										"&:hover": { color: "#d32f2f", backgroundColor: "rgba(211, 47, 47, 0.1)" },
									}}
								>
									<DeleteIcon fontSize="small" />
								</Button>
							</Box>
						</>
					)}
				</Box>

				<Box sx={{ height: "20px", mt: 1 }}>
					{files.length > 0 && (
						<Typography
							variant="caption"
							sx={{
								color: "#1D54A2",
								fontSize: "12px",
								mt: 1,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<Box component="span" sx={{ mr: 0.5 }}>
								✓
							</Box>
							The file is ready for import.
						</Typography>
					)}
				</Box>
			</Box>

			{/* Historical / backdated period option */}
			{files.length > 0 && (
				<Box
					sx={{
						mt: 1,
						p: 1.5,
						borderRadius: "10px",
						border: "1px solid #e0e0e0",
						backgroundColor: "#fafcff",
					}}
				>
					<FormControlLabel
						control={
							<Checkbox
								size="small"
								checked={isHistoricalImport}
								onChange={(e) => {
									setIsHistoricalImport(e.target.checked);
									if (!e.target.checked) setHistoricalPeriod("");
								}}
								sx={{
									color: "#1D54A2",
									"&.Mui-checked": { color: "#1D54A2" },
								}}
							/>
						}
						label={
							<Typography variant="body2" sx={{ fontSize: "13px", color: "#333" }}>
								This is an import for a past period/cutoff.
							</Typography>
						}
					/>

					<Collapse in={isHistoricalImport}>
						<Box sx={{ mt: 1, pl: 4 }}>
							<TextField
								label="Period"
								type="month"
								size="small"
								value={historicalPeriod}
								onChange={(e) => setHistoricalPeriod(e.target.value)}
								InputLabelProps={{ shrink: true }}
								inputProps={{ max: maxHistoricalPeriod }}
								sx={{ minWidth: 200 }}
							/>
							<Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "#666", fontSize: "12px" }}>
								{historicalPeriod
									? `Records will be saved to ${formatPeriodReadable(historicalPeriod)} cutoff only. Current live data won't change.`
									: "Choose the month this data belongs to. Current live data won't change."}
							</Typography>
						</Box>
					</Collapse>
				</Box>
			)}

			<Stack direction={{ xs: "column", sm: "row" }} alignItems={"center !important"} justifyContent={"flex-end"}>
				<Box mt={1}>
					<Button
						variant="contained"
						onClick={handleSubmit}
						disabled={files.length === 0}
						size="small"
						sx={{
							textTransform: "none",
							paddingX: 2,
							paddingY: 0.5,
							fontSize: "0.8rem",
							backgroundColor: "#1D54A2",
							borderRadius: "12px",
							"&:hover": { backgroundColor: "#163f7c" },
							"&.Mui-disabled": { backgroundColor: "#1D54A2", color: "#fff", opacity: 0.6 },
						}}
					>
						Submit
					</Button>
				</Box>
			</Stack>

			{/* Confirmation Dialog */}
			<Dialog
				open={openConfirm}
				onClose={handleCancel}
				maxWidth="xs"
				fullWidth
				PaperProps={{
					sx: {
						borderRadius: "12px",
						backgroundColor: "#f9fbfd",
						p: 2,
						textAlign: "center",
						border: "1px solid #e0e0e0",
						boxShadow: "0px 4px 12px rgba(0,0,0,0.06)",
					},
				}}
			>
				<DialogTitle sx={{ display: "flex", justifyContent: "center", p: 0.5 }}>
					<Warning sx={{ color: "#f59e0b", fontSize: 36 }} />
				</DialogTitle>
				<DialogContent>
					<Typography sx={{ mb: 1.5, fontSize: "0.85rem", color: "text.secondary", lineHeight: 1.4 }}>{confirmationMessage}</Typography>
				</DialogContent>
				<DialogActions sx={{ justifyContent: "center", gap: 1.5, pb: 1.5 }}>
					<Button
						onClick={handleCancel}
						sx={{
							color: "#e74c3c",
							backgroundColor: "#fff !important",
							textTransform: "none",
							border: "1px solid #e0e0e0",
							borderRadius: "8px",
							fontSize: "0.8rem",
							px: 2,
							"&:hover": { backgroundColor: "#fbe9e7", borderColor: "#e74c3c", color: "#c0392b" },
						}}
					>
						Cancel
					</Button>
					<Button
						onClick={handleConfirm}
						variant="contained"
						sx={{
							backgroundColor: "#1D54A2",
							opacity: 0.6,
							color: "#fff",
							textTransform: "none",
							borderRadius: "8px",
							fontSize: "0.8rem",
							px: 2.5,
							"&:hover": { backgroundColor: "#1D54A2", opacity: 1 },
						}}
					>
						Confirm
					</Button>
				</DialogActions>
			</Dialog>

			{/* Upload Status Modal */}
			<Dialog
				open={isUploading}
				onClose={() => {}}
				maxWidth="sm"
				fullWidth
				disableEscapeKeyDown
				PaperProps={{
					sx: {
						borderRadius: "12px",
						backgroundColor: "#f9fbfd",
						border: "1px solid #e0e0e0",
						p: 0,
						overflow: "hidden",
					},
				}}
			>
				<DialogTitle
					disableTypography
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						gap: 1,
						borderBottom: "1px solid #e0e0e0",
						backgroundColor: "#fff",
						py: 1,
						px: 2,
						mb: 1,
					}}
				>
					<Typography variant="h6" sx={{ fontSize: "1.875rem", fontWeight: 700, color: "#1D54A2" }}>
						Import Status
					</Typography>
				</DialogTitle>
				<DialogContent sx={{ px: 3, py: 2 }}>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
						{uploadStats.isProcessing && uploadStats.total > 0 && (
							<Box sx={{ mb: 2 }}>
								<Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
									<Typography variant="caption" sx={{ fontSize: "12px", color: "#666" }}>
										Progress
									</Typography>
									<Typography variant="caption" sx={{ fontSize: "12px", color: "#666" }}>
										{uploadStats.currentRecord}/{uploadStats.total}
									</Typography>
								</Box>
								<LinearProgress
									variant="determinate"
									value={(uploadStats.currentRecord / uploadStats.total) * 100}
									sx={{
										height: 8,
										borderRadius: 4,
										backgroundColor: "#f0f0f0",
										"& .MuiLinearProgress-bar": { backgroundColor: "#1D54A2", borderRadius: 4 },
									}}
								/>
							</Box>
						)}

						{isUploading && (
							<Box
								sx={{
									backgroundColor: "#fff",
									p: 2.5,
									borderRadius: "12px",
									border: "1px solid #e0e0e0",
									boxShadow: "0px 2px 4px rgba(0,0,0,0.04)",
									minWidth: "270px",
								}}
							>
								<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
									<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
										<Typography variant="body2" sx={{ color: "#666", fontSize: "13px" }}>
											Total Records:
										</Typography>
										<Typography variant="body2" sx={{ color: "#333", fontWeight: "600", fontSize: "13px" }}>
											{uploadStats.total || 0}
										</Typography>
									</Box>

									<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
										<Typography variant="body2" sx={{ color: "#666", fontSize: "13px" }}>
											Successful Imports:
										</Typography>
										<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
											{uploadStats.isProcessing && !uploadStats.isComplete ? (
												<CircularProgress size={16} sx={{ color: "#2e7d32" }} />
											) : (
												<Typography variant="body2" sx={{ color: "#2e7d32", fontWeight: 600, fontSize: "13px" }}>
													{uploadStats.successful}
												</Typography>
											)}
										</Box>
									</Box>

									<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
										<Typography variant="body2" sx={{ color: "#666", fontSize: "13px" }}>
											Failed Imports:
										</Typography>
										<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
											{uploadStats.isProcessing && !uploadStats.isComplete ? (
												<CircularProgress size={16} sx={{ color: "#d32f2f" }} />
											) : (
												<Typography
													variant="body2"
													sx={{ color: uploadStats.failed > 0 ? "#d32f2f" : "#666", fontWeight: 600, fontSize: "13px" }}
												>
													{uploadStats.failed}
												</Typography>
											)}
										</Box>
									</Box>
								</Box>

								<Box sx={{ mt: 2 }}>
									<Box
										sx={{
											width: "100%",
											backgroundColor: "#f0f0f0",
											borderRadius: "8px",
											height: 6,
											overflow: "hidden",
											mb: 1,
										}}
									>
										<Box
											sx={{
												width: uploadStats.total > 0 ? `${(uploadStats.successful / uploadStats.total) * 100}%` : "0%",
												backgroundColor: uploadStats.failed > 0 ? "#ff9800" : "#4caf50",
												height: "100%",
												borderRadius: "8px",
												transition: "width 0.5s ease-out",
											}}
										/>
									</Box>
									<Typography
										variant="caption"
										sx={{ display: "block", textAlign: "center", color: "#666", fontSize: "12px", fontWeight: "500" }}
									>
										{uploadStats.total > 0
											? `${Math.round((uploadStats.successful / uploadStats.total) * 100)}% Success Rate`
											: "0% Success Rate"}
									</Typography>
								</Box>
							</Box>
						)}

						{/* Policy rows that landed nowhere. Every row is matched to an agent
						    by the NPN in its policy_aor cell, so a typo there means a policy
						    nobody gets paid for — and nothing else on this screen would
						    reveal it. Opt-in state is not a reason to appear here: an
						    opted-out agent's book is still imported, it just earns nothing. */}
						{unmatchedRows && (
							<Alert
								severity="warning"
								icon={<Warning fontSize="inherit" />}
								sx={{ mt: 2, borderRadius: "12px", textAlign: "left" }}
							>
								<Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>
									{unmatchedRows.total} policy row{unmatchedRows.total === 1 ? " was" : "s were"} not imported
								</Typography>
								<Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1 }}>
									These name an agent the roster does not have, so nobody will be paid for them. Check the policy_aor cell against
									Staff_Db and re-import once it matches — nothing already stored has been changed.
								</Typography>
								<Box component="ul" sx={{ m: 0, pl: 2.5 }}>
									{unmatchedRows.reasons.map((entry) => (
										<Box component="li" key={`${entry.reason}-${entry.label}`} sx={{ fontSize: 12 }}>
											<strong>{entry.label || "(blank)"}</strong> — {entry.rows} row{entry.rows === 1 ? "" : "s"},{" "}
											{entry.reason === "unknown_npn" ? "no agent in Staff_Db has that NPN" : "the policy_aor cell carries no NPN"}
										</Box>
									))}
								</Box>
							</Alert>
						)}

						{/* In-service agents this workbook carried no policies for. Their
						    opt-in state and stored history are untouched, but they will read
						    $0 for this cutoff — and an agent left out by mistake looks
						    exactly like one who simply had a quiet month. */}
						{agentsWithoutRows && (
							<Alert severity="info" sx={{ mt: 2, borderRadius: "12px", textAlign: "left" }}>
								<Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>
									{agentsWithoutRows.total} agent{agentsWithoutRows.total === 1 ? "" : "s"} in service had no policies in this file
								</Typography>
								<Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1 }}>
									Nothing they already had is lost — their existing book of business and every past cutoff are kept exactly as they
									were, since an import only ever adds records and never overwrites or clears them. It is only this period that has no
									book, so they will show no members and no earnings for it.
								</Typography>
								<Box component="ul" sx={{ m: 0, pl: 2.5 }}>
									{agentsWithoutRows.agents.map((agent) => (
										<Box component="li" key={agent.npn} sx={{ fontSize: 12 }}>
											<strong>{agent.name || "(unnamed row)"}</strong> — NPN {agent.npn}
										</Box>
									))}
								</Box>
							</Alert>
						)}

						{/* New accounts the welcome mail never reached. The import itself is
						    already committed and their books are in — what is missing is the
						    invitation, and without it they have no way to set a password.
						    Nothing re-tries this, so it has to be done by hand. */}
						{welcomeEmailFailures && (
							<Alert
								severity="warning"
								icon={<Warning fontSize="inherit" />}
								sx={{ mt: 2, borderRadius: "12px", textAlign: "left" }}
							>
								<Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>
									{welcomeEmailFailures.length} new agent{welcomeEmailFailures.length === 1 ? "" : "s"} could not be sent a welcome
									email
								</Typography>
								<Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1 }}>
									Their accounts were created and their policies imported — only the email failed. Until they get a link they cannot
									set a password or sign in. Fix the address in Staff_Db if it is wrong, then send them a setup link from Agent
									Management; re-importing will not try again, since the account now exists.
								</Typography>
								<Box component="ul" sx={{ m: 0, pl: 2.5 }}>
									{welcomeEmailFailures.map((agent) => (
										<Box component="li" key={`${agent.name}-${agent.email}`} sx={{ fontSize: 12 }}>
											<strong>{agent.name || "(unnamed row)"}</strong> — {agent.email || "(no email on file)"}: {agent.reason}
										</Box>
									))}
								</Box>
							</Alert>
						)}

						{uploadStats.isComplete && (
							<Box
								sx={{
									textAlign: "center",
									mt: 1,
									p: 2,
									backgroundColor: "#fff",
									borderRadius: "12px",
									border: "1px solid #e0e0e0",
								}}
							>
								{uploadStats.failed === 0 && uploadStats.successful > 0 ? (
									<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
										<Box
											sx={{
												width: 20,
												height: 20,
												borderRadius: "50%",
												backgroundColor: "#4caf50",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												fontSize: "12px",
											}}
										>
											✓
										</Box>
										<Typography variant="body2" sx={{ color: "#2e7d32", fontWeight: "500", fontSize: "14px" }}>
											All records imported successfully!
										</Typography>
									</Box>
								) : uploadStats.successful > 0 ? (
									<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
										<Box
											sx={{
												width: 20,
												height: 20,
												borderRadius: "50%",
												backgroundColor: "#ff9800",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												fontSize: "12px",
												color: "#fff",
											}}
										>
											!
										</Box>
										<Typography variant="body2" sx={{ color: "#ff9800", fontWeight: "500", fontSize: "14px" }}>
											Import completed with some fails
										</Typography>
									</Box>
								) : (
									<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
										<Box
											sx={{
												width: 20,
												height: 20,
												borderRadius: "50%",
												backgroundColor: "#d32f2f",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												fontSize: "12px",
												color: "#fff",
											}}
										>
											✕
										</Box>
										<Typography variant="body2" sx={{ color: "#d32f2f", fontWeight: "500", fontSize: "14px" }}>
											Import failed
										</Typography>
									</Box>
								)}
							</Box>
						)}
					</Box>
				</DialogContent>

				{uploadStats.isComplete && (
					<DialogActions
						sx={{
							justifyContent: "center",
							pb: 3,
							px: 3,
							backgroundColor: "#fff",
							borderTop: "1px solid #e0e0e0",
						}}
					>
						<Button
							onClick={() => {
								setIsUploading(false);
								handleDelete(0);
								resetUploadState();
							}}
							variant="contained"
							sx={{
								backgroundColor: "#1D54A2 !important",
								color: "#fff",
								textTransform: "none",
								borderRadius: "12px",
								px: 4,
								py: 1,
								fontSize: "14px",
								fontWeight: "500",
								"&:hover": { backgroundColor: "#163f7c !important" },
							}}
						>
							Close
						</Button>
					</DialogActions>
				)}
			</Dialog>

			<ExistingRowConflictDialog
				open={openConflictDialog}
				duplicates={duplicates}
				onClose={() => setOpenConflictDialog(false)}
				onConfirm={async (result) => {
					handleConfirmSubmitResolution(result);
				}}
				loading={submitConflictLoading}
			/>

			<SnackbarNotification
				open={snackbar.open}
				message={snackbar.message}
				severity={snackbar.severity}
				onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
			/>
		</Box>
	);
};

export default ImportExcel;
